import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SpeedIcon from '../icons/SpeedIcon';
import PowerIcon from '../icons/PowerIcon';
import AgilityIcon from '../icons/AgilityIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import CombineRating from '../types/CombineRating';
import EventRating from '../types/EventRating';
import Player from '../types/Player';
import Grid from '@material-ui/core/Grid';
import PowerRecruitIcon from '../icons/PowerRecruitIcon';
import SpeedRecruitIcon from '../icons/SpeedRecruitIcon';
import { addSpaceBetweenNumAndAlpha } from '../services/player';
import { COLOR_BLACK, COLOR_WHITE, COLOR_GREEN } from '../styles/colors';

interface PlayerCombineRatingProps {
  className?: string;
  combineRatings?: CombineRating[];
  isNFL?: boolean;
  player?: Player;
}

export const RECRUIT_TOOLTIP = {
  POWER: 'Player exceeds 75th percentile Division I position performance rating in vertical and or broad jump.',
  SPEED: 'Player exceeds 75th percentile Division I position performance rating in shuttle, 3-cone, or 40 yard dash.',
};

const useStyles = makeStyles((theme:Theme) => ({
  playerCombineRating: {},
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    fontWeight: 700,
  },
  headerIcon: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '44px',
    height: '44px',
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_BLACK}`,
    backgroundColor: COLOR_GREEN,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_WHITE,
    
    [theme.breakpoints.down('md')]: {
      width: 34,
      height: 34,
      fontSize: theme.typography.pxToRem(12),
    },
  },
  gridContainer: {
    alignItems: 'center',
  },
  rowContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    padding: 0,
    marginLeft: 'auto',
  },
}), { name: PlayerCombineRating.name });

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

function aggregateAllEventsOfType (eventType:string, combineRatings: CombineRating[]):EventRating[] {
  return combineRatings
    // @ts-ignore
    .flatMap((combine:CombineRating) => combine[eventType] as EventRating[] || []);
}

function getTheBestEvent (events:EventRating[]):EventRating {
  const sortedEvents = !!events?.length
    ? events
      .sort((firstEvent:EventRating, secondEvent:EventRating) => {
        if (firstEvent?.percentile > secondEvent?.percentile) return -1;
        if (firstEvent?.percentile < secondEvent?.percentile) return 1;
        return 0;
      })
    : [];

  const firstEvent = sortedEvents[0];
  const anotherEventWithTheSamePercentileIndex = sortedEvents?.length > 1
    ? sortedEvents.slice(1).findIndex((event:EventRating) => {
        return event.percentile === firstEvent.percentile && event.name !== firstEvent.name;
      }) + 1
    : 0;

  // show 2 event names if their results are the same (edge case)
  if (anotherEventWithTheSamePercentileIndex > 0) {
    return {
      name: `${firstEvent?.name}/${sortedEvents[anotherEventWithTheSamePercentileIndex]?.name}`,
      percentile: firstEvent.percentile,
    } as EventRating;
  }

  return firstEvent;
}

export default function PlayerCombineRating (props: PlayerCombineRatingProps) {
  const {
    className,
    combineRatings,
    isNFL,
    player
  } = props;
  const classes = useStyles();

  if (!combineRatings?.length) return null;

  const bestSpeedEvent = getTheBestEvent(aggregateAllEventsOfType('speed', combineRatings));
  const bestPowerEvent = getTheBestEvent(aggregateAllEventsOfType('power', combineRatings));
  const bestAgilityEvent = getTheBestEvent(aggregateAllEventsOfType('agility', combineRatings));
  const noEvents = !bestPowerEvent && !bestSpeedEvent && !bestAgilityEvent;

  function isSpeedRecruitEnable ():boolean {
    if (player && player?.combineSpeedTier >= 4) {
      return true;
    }
    return false;
  }
  
  function isPowerRecruitEnable ():boolean {
    if (player && player?.combinePowerTier >= 4) {
      return true;
    }
    return false;
  }

  function isSpeedRecruitEnableForAgility ():boolean {
    if (player && player?.combineAgilityTier >= 4) {
      return true;
    }
    return false;
  }

  if (noEvents) return null;

  return (
    <NamedBox
      className={clsx(classes.playerCombineRating, className)}
      name={
        <div className={classes.header}>
          <div className={classes.headerIcon}>COM</div>

          <span>
            High School Combine Rating
            {/* {isNFL
              ? 'High School Combine Rating'
              : 'High School Combine Rating'
            } */}
          </span>
        </div>
      }
    >
      {bestSpeedEvent && (
        <ListItem
          icon={SpeedIcon}
          title='Speed:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid item lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{ addSpaceBetweenNumAndAlpha(bestSpeedEvent.name)}</b>

                {
                  isSpeedRecruitEnable() && (
                    <div className={classes.icon}>
                      <Tooltip title={RECRUIT_TOOLTIP.SPEED}>
                        <SpeedRecruitIcon className={'green'} padding={'0'} />
                      </Tooltip>
                    </div>
                  )
                }
              </div>
            </Grid>
            <Grid lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestSpeedEvent.percentile}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}

      {bestPowerEvent && (
        <ListItem
          icon={PowerIcon}
          title='Power:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{ addSpaceBetweenNumAndAlpha(bestPowerEvent.name)}</b>

                {
                  isPowerRecruitEnable() && (
                    <div className={classes.icon}>
                      <Tooltip title={RECRUIT_TOOLTIP.POWER}>
                        <PowerRecruitIcon className={'green'} padding={'0'} />
                      </Tooltip>
                    </div>
                  )
                }
              </div>
            </Grid>
            <Grid lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestPowerEvent.percentile}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}

      {bestAgilityEvent && (
        <ListItem
          icon={AgilityIcon}
          title='Agility:'
        >
          <Grid container className={classes.gridContainer}>
            <Grid lg={4} sm={5} xs={12}>
              <div className={classes.rowContent}>
                <b>{bestAgilityEvent.name}</b>

                
                {
                  isSpeedRecruitEnableForAgility() && (
                    <div className={classes.icon}>
                      <Tooltip title={RECRUIT_TOOLTIP.SPEED}>
                        <SpeedRecruitIcon className={'green'} padding={'0'} />
                      </Tooltip>
                    </div>
                  )
                }
              </div>
            </Grid>
            <Grid lg xs>
              <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={bestAgilityEvent.percentile}
                  />
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </ListItem>
      )}
    </NamedBox>
  );
}
